// Shared (client-safe) logic for grammar grading: which task types get a grammar evaluation, and the
// per-exam Fehlerindex (FI) deduction settings. The FI-vs-rules mode resolution itself is per-task
// and lives in shared/grammar-rules-resolution.ts, since it depends on each task's own detected
// grammar rules (not just exam-wide settings).

import type { GradingScale } from './grading-scales.js';

/** Task types that get a grammar/spelling evaluation (long continuous-text essays). */
export const GRAMMAR_TASK_TYPES = new Set<string>([
  'ESSAY',
  'MATERIAL_BASED_ESSAY',
  'LITERARY_ANALYSIS_POETRY',
  'ANALYSIS_NON_FICTION',
]);

export interface GrammarFiThreshold {
  /** Deduction applies when the student's aggregate FI is strictly greater than this value. */
  minFi: number;
  /** Grade steps to deduct (one step = one border in the grading scale). */
  steps: number;
}

export interface GrammarFiSettings {
  fiEnabled: boolean;
  thresholds: GrammarFiThreshold[];
  /**
   * Per-exam override of whether to always use FI instead of detected/merged grammar rules.
   * `true`/`false` forces that choice for this exam regardless of the teacher's default
   * (Teacher.grammarPreferences.alwaysUseFi) or whether rules were detected; `null`/absent inherits
   * the teacher's default. See shared/grammar-rules-resolution.ts.
   */
  alwaysUseFi?: boolean | null;
}

export const DEFAULT_GRAMMAR_FI_SETTINGS: GrammarFiSettings = {
  fiEnabled: false,
  thresholds: [
    { minFi: 3, steps: 1 },
    { minFi: 6, steps: 2 },
  ],
  alwaysUseFi: null,
};

export function parseGrammarFiSettings(json: string | null | undefined): GrammarFiSettings {
  if (!json) return DEFAULT_GRAMMAR_FI_SETTINGS;
  try {
    const parsed = JSON.parse(json) as GrammarFiSettings;
    if (typeof parsed?.fiEnabled !== 'boolean' || !Array.isArray(parsed.thresholds)) {
      return DEFAULT_GRAMMAR_FI_SETTINGS;
    }
    const thresholds = parsed.thresholds
      .filter(t => Number.isFinite(t?.minFi) && Number.isFinite(t?.steps) && t.steps > 0)
      .sort((a, b) => a.minFi - b.minFi);
    const alwaysUseFi = typeof parsed.alwaysUseFi === 'boolean' ? parsed.alwaysUseFi : null;
    return { fiEnabled: parsed.fiEnabled, thresholds, alwaysUseFi };
  } catch {
    return DEFAULT_GRAMMAR_FI_SETTINGS;
  }
}

/** Grade steps to deduct for an aggregate FI. 0 when no threshold is exceeded. Callers already gate
 * on whether FI actually applies to this exam (see taskGrammarMode/resolveAlwaysUseFi) before calling
 * this — there is no separate "deduction enabled" toggle anymore. */
export function fiDeductionSteps(fi: number, settings: GrammarFiSettings): number {
  if (!Number.isFinite(fi)) return 0;
  let steps = 0;
  for (const threshold of settings.thresholds) {
    if (fi > threshold.minFi) steps = Math.max(steps, threshold.steps);
  }
  return steps;
}

/**
 * Shift a grade label by `steps` positions toward the worst grade of the scale (borders are sorted
 * best → worst). Clamps at the worst grade. No-op for percentage scales (no discrete steps) and for
 * labels not found in the scale.
 */
export function applyGradeStepDeduction(gradeLabel: string, steps: number, scale: GradingScale): string {
  if (steps <= 0 || scale.type === 'percentage') return gradeLabel;
  const index = scale.borders.findIndex(b => b.label === gradeLabel);
  if (index === -1) return gradeLabel;
  const shifted = Math.min(index + steps, scale.borders.length - 1);
  return scale.borders[shifted]?.label ?? gradeLabel;
}

